/* ==========================================================================
   iTechVista — slideshow behaviour
   assets/itv-slideshow.js   (pairs with sections/itv-slideshow.liquid)
   --------------------------------------------------------------------------
   Original code for itechvista-v3. No dependencies, no Dawn components.

   Shipped as a custom element rather than a DOMContentLoaded initialiser so
   that the theme editor gets teardown and re-init for free: re-rendering a
   section replaces the element, which fires disconnectedCallback (timer
   cleared, observers disconnected) and then connectedCallback on the new one.

   Autoplay policy, in one place:
     - never starts when prefers-reduced-motion: reduce is set, and stops
       immediately if the visitor turns that preference on mid-visit;
     - pauses on hover, on focus inside the slideshow, while the tab is
       hidden, while the slideshow is scrolled out of view, and while the
       merchant has a slide selected in the theme editor;
     - is always interruptible by the rendered pause button.

   The aria-live region is 'polite' only while autoplay is idle. Announcing an
   unattended rotation every few seconds would talk over the rest of the page,
   so during autoplay the region is muted and re-armed the moment it stops.
   ========================================================================== */

(function () {
  'use strict';

  var REDUCE_QUERY = '(prefers-reduced-motion: reduce)';

  class ItvSlideshow extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;

      this.track = this.querySelector('[data-itv-track]');
      this.slides = Array.prototype.slice.call(this.querySelectorAll('[data-itv-slide]'));
      if (!this.track || this.slides.length === 0) return;

      this.status = this.querySelector('[data-itv-status]');
      this.counter = this.querySelector('[data-itv-current]');
      this.markers = Array.prototype.slice.call(this.querySelectorAll('[data-itv-goto]'));
      this.toggleButton = this.querySelector('[data-itv-toggle]');

      this.index = 0;
      this.speed = Math.max(3000, parseInt(this.dataset.speed, 10) || 5000);
      this.wantsAutoplay = this.dataset.autoplay === 'true' && this.slides.length > 1;
      this.timer = null;
      this.paused = { user: false, hover: false, focus: false, hidden: false, offscreen: false, editor: false };
      this.ready = true;

      this.reduceQuery = window.matchMedia(REDUCE_QUERY);

      this.onPrev = this.prev.bind(this);
      this.onNext = this.next.bind(this);
      this.onMarker = this.handleMarker.bind(this);
      this.onKeydown = this.handleKeydown.bind(this);
      this.onEnter = this.setFlag.bind(this, 'hover', true);
      this.onLeave = this.setFlag.bind(this, 'hover', false);
      this.onFocusIn = this.setFlag.bind(this, 'focus', true);
      this.onFocusOut = this.handleFocusOut.bind(this);
      this.onVisibility = this.handleVisibility.bind(this);
      this.onToggle = this.handleToggle.bind(this);
      this.onMotionChange = this.handleMotionChange.bind(this);
      this.onBlockSelect = this.handleBlockSelect.bind(this);
      this.onBlockDeselect = this.setFlag.bind(this, 'editor', false);

      var prev = this.querySelector('[data-itv-prev]');
      var next = this.querySelector('[data-itv-next]');
      if (prev) prev.addEventListener('click', this.onPrev);
      if (next) next.addEventListener('click', this.onNext);
      this.markers.forEach(function (marker) {
        marker.addEventListener('click', this.onMarker);
      }, this);
      if (this.toggleButton) this.toggleButton.addEventListener('click', this.onToggle);

      this.addEventListener('keydown', this.onKeydown);
      this.addEventListener('mouseenter', this.onEnter);
      this.addEventListener('mouseleave', this.onLeave);
      this.addEventListener('focusin', this.onFocusIn);
      this.addEventListener('focusout', this.onFocusOut);
      this.addEventListener('shopify:block:select', this.onBlockSelect);
      this.addEventListener('shopify:block:deselect', this.onBlockDeselect);
      document.addEventListener('visibilitychange', this.onVisibility);

      if (typeof this.reduceQuery.addEventListener === 'function') {
        this.reduceQuery.addEventListener('change', this.onMotionChange);
      } else if (typeof this.reduceQuery.addListener === 'function') {
        this.reduceQuery.addListener(this.onMotionChange);
      }

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          function (entries) {
            this.setFlag('offscreen', !entries[0].isIntersecting);
          }.bind(this),
          { threshold: 0.2 }
        );
        this.observer.observe(this);
      }

      this.render(false);
      this.refreshAutoplay();
    }

    disconnectedCallback() {
      this.stopTimer();
      if (this.observer) this.observer.disconnect();
      document.removeEventListener('visibilitychange', this.onVisibility);
      if (this.reduceQuery) {
        if (typeof this.reduceQuery.removeEventListener === 'function') {
          this.reduceQuery.removeEventListener('change', this.onMotionChange);
        } else if (typeof this.reduceQuery.removeListener === 'function') {
          this.reduceQuery.removeListener(this.onMotionChange);
        }
      }
      this.ready = false;
    }

    /* ------------------------------------------------------------ movement */

    goTo(target, userInitiated) {
      var count = this.slides.length;
      if (count === 0) return;
      this.index = ((target % count) + count) % count;
      this.render(userInitiated);
      if (userInitiated) this.refreshAutoplay();
    }

    next(event) {
      if (event) event.preventDefault();
      this.goTo(this.index + 1, Boolean(event));
    }

    prev(event) {
      if (event) event.preventDefault();
      this.goTo(this.index - 1, Boolean(event));
    }

    render(userInitiated) {
      var index = this.index;

      this.track.style.transform = 'translateX(' + -100 * index + '%)';

      this.slides.forEach(function (slide, i) {
        var active = i === index;
        slide.dataset.active = active ? 'true' : 'false';
        slide.toggleAttribute('inert', !active);
        if (active) {
          slide.removeAttribute('aria-hidden');
        } else {
          slide.setAttribute('aria-hidden', 'true');
        }
      });

      this.markers.forEach(function (marker) {
        if (parseInt(marker.dataset.itvGoto, 10) === index) {
          marker.setAttribute('aria-current', 'true');
        } else {
          marker.removeAttribute('aria-current');
        }
      });

      if (this.counter) this.counter.textContent = String(index + 1);

      if (this.status) {
        var heading = this.slides[index].querySelector('.itv-slides__heading');
        var label = 'Slide ' + (index + 1) + ' of ' + this.slides.length;
        if (heading && heading.textContent.trim()) label += ': ' + heading.textContent.trim();
        // Only speak when a person asked for the change; unattended rotation
        // is muted at the region level in refreshAutoplay().
        if (userInitiated || !this.running) this.status.textContent = label;
      }
    }

    handleMarker(event) {
      event.preventDefault();
      var target = parseInt(event.currentTarget.dataset.itvGoto, 10);
      if (!isNaN(target)) this.goTo(target, true);
    }

    handleKeydown(event) {
      if (this.slides.length < 2) return;
      var tag = event.target && event.target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;

      switch (event.key) {
        case 'ArrowLeft':
          event.preventDefault();
          this.goTo(this.index - 1, true);
          break;
        case 'ArrowRight':
          event.preventDefault();
          this.goTo(this.index + 1, true);
          break;
        case 'Home':
          event.preventDefault();
          this.goTo(0, true);
          break;
        case 'End':
          event.preventDefault();
          this.goTo(this.slides.length - 1, true);
          break;
        default:
          break;
      }
    }

    /* ------------------------------------------------------------ autoplay */

    setFlag(name, value) {
      if (this.paused[name] === value) return;
      this.paused[name] = value;
      this.refreshAutoplay();
    }

    handleFocusOut(event) {
      if (this.contains(event.relatedTarget)) return;
      this.setFlag('focus', false);
    }

    handleVisibility() {
      this.setFlag('hidden', document.hidden);
    }

    handleMotionChange() {
      this.refreshAutoplay();
    }

    handleToggle(event) {
      event.preventDefault();
      this.paused.user = !this.paused.user;
      this.refreshAutoplay();
    }

    handleBlockSelect(event) {
      var slide = event.target.closest ? event.target.closest('[data-itv-slide]') : null;
      if (slide) this.goTo(parseInt(slide.dataset.index, 10) || 0, true);
      this.setFlag('editor', true);
    }

    blocked() {
      if (!this.wantsAutoplay) return true;
      if (this.reduceQuery && this.reduceQuery.matches) return true;
      var flags = this.paused;
      return flags.user || flags.hover || flags.focus || flags.hidden || flags.offscreen || flags.editor;
    }

    refreshAutoplay() {
      var shouldRun = !this.blocked();

      if (shouldRun && !this.timer) {
        this.timer = window.setInterval(
          function () {
            this.goTo(this.index + 1, false);
          }.bind(this),
          this.speed
        );
      } else if (!shouldRun) {
        this.stopTimer();
      }

      this.running = Boolean(this.timer);

      // Muted while it rotates on its own, polite again as soon as it stops.
      if (this.status) this.status.setAttribute('aria-live', this.running ? 'off' : 'polite');

      if (this.toggleButton) {
        var playing = this.running;
        var label = playing
          ? this.toggleButton.dataset.labelPause
          : this.toggleButton.dataset.labelPlay;
        this.toggleButton.setAttribute('aria-label', label || '');
        this.toggleButton.setAttribute('aria-pressed', this.paused.user ? 'true' : 'false');
        this.toggleButton.dataset.playing = playing ? 'true' : 'false';
      }
    }

    stopTimer() {
      if (this.timer) {
        window.clearInterval(this.timer);
        this.timer = null;
      }
      this.running = false;
    }
  }

  if (typeof window.customElements !== 'undefined' && !window.customElements.get('itv-slideshow')) {
    window.customElements.define('itv-slideshow', ItvSlideshow);
  }
})();
